// src/ui/settings/accessibility-options.ts
//
// Steam port T-6.7b + T-6.7c — pure-logic helpers for the Settings modal's
// Accessibility section (colorblind profile dropdown, shape-overlay
// toggle, low-motion toggle). DOM- and IPC-free so unit tests can
// exercise the encode/decode + filter rules without jsdom or an Electron
// runtime; the React component owns side effects and the persistence
// module owns the SaveFile write-through.
//
// **Spec reference:** `steam-port/IMPLEMENTATION_DETAILED.md`
//   - T-6.7b lines ~6932-6977 (colorblind profiles + shape overlay).
//   - T-6.7c lines ~6978-7058 (low-motion + Frenzy luminance acceptance).
//
// **Schema fields touched** (DEFINITIONS §6, nested under `settings`):
//   - `colorblind_profile: 'none' | 'deuteranopia' | 'protanopia' | 'tritanopia'`
//   - `shape_overlay: boolean`           (T-6.7b — additive, v1-compatible)
//   - `low_motion: boolean`              (T-2.4 already shipped this key;
//                                         the task brief calls it
//                                         `reduce_motion` but DEFINITIONS §6
//                                         and the existing schema use
//                                         `low_motion` — we honor the
//                                         existing canonical name to avoid
//                                         schema-key drift)
//
// **Per `feedback_error_text_immersion`**: every player-facing string in
// this module reads as steampunk-clocktower voice. No raw error / IPC
// channel names ever reach the player.

// ---------------------------------------------------------------------------
// Types — mirror the persisted SaveFile shape per DEFINITIONS §6.
// ---------------------------------------------------------------------------

/** The four colorblind palette modes. `'none'` is the default (no remap).
 *  Persisted verbatim into `save.settings.colorblind_profile`. Matches the
 *  `z.enum([...])` in `src/saves/schema.ts`. */
export type ColorblindProfile =
  | 'none'
  | 'deuteranopia'
  | 'protanopia'
  | 'tritanopia';

/** Dropdown row — `value` is the canonical save-key spelling, `label` is
 *  the steampunk-themed display string. */
export interface ColorblindProfileOption {
  value: ColorblindProfile;
  label: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Dropdown options in canonical order. Off → most-common → least-common,
 *  which matches the Wong-2011 prevalence ordering. Labels are themed
 *  per `feedback_error_text_immersion`. */
export const COLORBLIND_PROFILE_OPTIONS: ReadonlyArray<ColorblindProfileOption> =
  [
    { value: 'none', label: 'Off (Standard Palette)' },
    { value: 'deuteranopia', label: 'Deuteranopia (Red-Green I)' },
    { value: 'protanopia', label: 'Protanopia (Red-Green II)' },
    { value: 'tritanopia', label: 'Tritanopia (Blue-Yellow)' },
  ];

/** All valid colorblind-profile values, in dropdown order. Useful for
 *  Zod enum tests + exhaustive switch checks. */
export const COLORBLIND_PROFILE_VALUES: ReadonlyArray<ColorblindProfile> =
  COLORBLIND_PROFILE_OPTIONS.map((o) => o.value);

/**
 * CSS `filter` strings applied to the top-level renderer container per
 * profile. Implementation choice: a single top-level CSS filter (vs.
 * per-element palette swap) — matches the spec's note that "PixiJS
 * renderer reads `settings.colorblind_profile`" and is the simplest
 * approach that doesn't fork the bubble palette tables. Each filter is
 * a Wong-2011 derived approximation; the goldens in T-6.9b lock the
 * concrete pixel deltas, so refining these matrices later is a
 * goldens-update operation, not a contract break.
 *
 * The matrix entries below are the standard daltonize approximations
 * commonly cited (Brettel/Vienot 1997 for the LMS shifts). `none` is
 * the identity — applying it is a no-op so the renderer can omit the
 * `filter:` declaration entirely when the profile is off.
 */
export const COLORBLIND_FILTER: Record<ColorblindProfile, string> = {
  none: 'none',
  // Deuteranopia simulation (green-deficient). Standard 5x4 matrix.
  deuteranopia:
    'url("#rc-cb-deuteranopia")',
  // Protanopia simulation (red-deficient).
  protanopia:
    'url("#rc-cb-protanopia")',
  // Tritanopia simulation (blue-deficient).
  tritanopia:
    'url("#rc-cb-tritanopia")',
};

/**
 * Inline SVG filter source — embedded once on the page so each
 * `filter: url('#rc-cb-*')` reference resolves without an extra HTTP
 * fetch. The matrices follow the Brettel/Vienot 1997 daltonize
 * approximations. Exported as a string so the React component can
 * render it via `dangerouslySetInnerHTML` once per app mount.
 *
 * The `id` values match `COLORBLIND_FILTER` above — keep them in sync.
 */
export const COLORBLIND_SVG_FILTERS = `
<svg xmlns="http://www.w3.org/2000/svg" width="0" height="0"
     style="position:absolute;width:0;height:0;overflow:hidden"
     aria-hidden="true">
  <defs>
    <filter id="rc-cb-deuteranopia">
      <feColorMatrix type="matrix" values="
        0.625 0.375 0    0 0
        0.7   0.3   0    0 0
        0     0.3   0.7  0 0
        0     0     0    1 0"/>
    </filter>
    <filter id="rc-cb-protanopia">
      <feColorMatrix type="matrix" values="
        0.567 0.433 0     0 0
        0.558 0.442 0     0 0
        0     0.242 0.758 0 0
        0     0     0     1 0"/>
    </filter>
    <filter id="rc-cb-tritanopia">
      <feColorMatrix type="matrix" values="
        0.95 0.05  0    0 0
        0    0.433 0.567 0 0
        0    0.475 0.525 0 0
        0    0    0    1 0"/>
    </filter>
  </defs>
</svg>
`.trim();

// ---------------------------------------------------------------------------
// Pure helpers — colorblind dropdown.
// ---------------------------------------------------------------------------

/** Coerce an arbitrary string to a `ColorblindProfile`, defaulting to
 *  `'none'` for unknown input. Robust against legacy / corrupt saves. */
export function coerceColorblindProfile(v: unknown): ColorblindProfile {
  return COLORBLIND_PROFILE_VALUES.includes(v as ColorblindProfile)
    ? (v as ColorblindProfile)
    : 'none';
}

/** Look up the display label for a profile. Used by the React component
 *  + tests to assert dropdown text. */
export function colorblindProfileLabel(profile: ColorblindProfile): string {
  const offLabel = COLORBLIND_PROFILE_OPTIONS[0]?.label ?? 'Off';
  return (
    COLORBLIND_PROFILE_OPTIONS.find((o) => o.value === profile)?.label ??
    offLabel
  );
}

// ---------------------------------------------------------------------------
// Shape overlay — `data-color-tag` mapping convention.
// ---------------------------------------------------------------------------

/** The set of color tags the renderer / UI can attach to elements. The
 *  values are keys on `BUBBLE_COLORS` in `src/renderer/colors.ts` —
 *  steampunk palette names — but the shape mapping is stable across
 *  profiles per spec ("shapes are cross-profile-stable so achromatopsia
 *  users see one consistent symbol system"). */
export type ColorTag =
  | 'red'
  | 'blue'
  | 'green'
  | 'yellow'
  | 'purple'
  | 'orange';

/** Cross-profile-stable shape glyph for each color tag, per spec line
 *  6959: red=circle, green=triangle, blue=square, yellow=diamond,
 *  purple=hexagon, orange=star. Used by the shape-overlay CSS sheet
 *  injected when `shape_overlay === true`. */
export const SHAPE_OVERLAY_GLYPHS: Record<ColorTag, string> = {
  red: '●',
  green: '▲',
  blue: '■',
  yellow: '◆',
  purple: '⬢',
  orange: '★',
};

/** All valid color tags, frozen for tests. */
export const COLOR_TAG_VALUES: ReadonlyArray<ColorTag> = [
  'red',
  'blue',
  'green',
  'yellow',
  'purple',
  'orange',
];

/** Build the CSS sheet that attaches a glyph badge to every element
 *  carrying a `data-color-tag` attribute when shape-overlay is on.
 *  Returns a string suitable for a `<style>` tag's `textContent`. The
 *  caller mounts/unmounts the sheet from the React layer based on
 *  `settings.shape_overlay`. */
export function buildShapeOverlayCss(): string {
  const rules = COLOR_TAG_VALUES.map(
    (tag) =>
      `[data-color-tag="${tag}"]::after { content: "${SHAPE_OVERLAY_GLYPHS[tag]}"; }`,
  ).join('\n');
  return `
/* T-6.7b shape overlay — attaches a cross-profile-stable glyph to every
   element with a data-color-tag attribute. Mounted by the Accessibility
   section when settings.shape_overlay === true. */
[data-color-tag]::after {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 0.85em;
  font-family: "Courier New", monospace;
  color: rgba(232, 220, 200, 0.95);
  text-shadow: 0 0 2px rgba(26, 21, 16, 0.9);
  pointer-events: none;
  user-select: none;
  z-index: 1;
}
${rules}
`.trim();
}

/** DOM mount-id for the shape-overlay <style> tag. Exported so tests can
 *  query for it deterministically. */
export const SHAPE_OVERLAY_STYLE_ID = 'rc-shape-overlay';

/** DOM mount-id for the colorblind SVG filter <svg> defs block. */
export const COLORBLIND_SVG_ID = 'rc-colorblind-filters';

// ---------------------------------------------------------------------------
// Low-motion — OS prefers-reduced-motion detection.
// ---------------------------------------------------------------------------

/** Read the OS-level `prefers-reduced-motion` media query. Returns
 *  `true` when the OS reports `reduce`. Robust against headless / SSR
 *  contexts (no `window.matchMedia`) — those return `false`.
 *
 *  This is the *default-seed* for the toggle on first boot; after the
 *  player explicitly picks a value, the persisted setting always wins.
 *  Spec T-6.7c step 1: "Honor the OS-level `prefers-reduced-motion`
 *  media query — default the toggle ON if the OS reports `reduce`." */
export function osPrefersReducedMotion(): boolean {
  if (typeof globalThis === 'undefined') return false;
  const w = globalThis as { matchMedia?: (q: string) => { matches: boolean } };
  if (typeof w.matchMedia !== 'function') return false;
  try {
    return w.matchMedia('(prefers-reduced-motion: reduce)').matches === true;
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Frenzy luminance acceptance — T-6.7c step 2.
// ---------------------------------------------------------------------------

/**
 * WCAG 2.3.3 / W3C photosensitive-epilepsy threshold: max
 * frame-to-frame luminance delta when `settings.low_motion === true`.
 * Spec T-6.7c step 6.7c.2 acceptance: "no frame-to-frame luminance jumps >10%".
 *
 * The luminance of a frame is the average of the per-pixel
 * `0.299·R + 0.587·G + 0.114·B` in [0, 1]. The renderer's Frenzy effects
 * gate every per-frame intensity bump through `clampLuminanceDelta` when
 * low-motion is on so the synthetic-frame harness stays under this cap.
 */
export const MAX_LUMINANCE_DELTA = 0.1;

/** Compute the relative luma of an [r, g, b] triple in [0, 1]. The
 *  Rec.601 coefficients used here match the test harness in
 *  `desktop/tests/low-motion-luminance.spec.ts`. */
export function relativeLuma(r: number, g: number, b: number): number {
  const clamp = (x: number) => Math.min(1, Math.max(0, x));
  return clamp(0.299) * clamp(r) + clamp(0.587) * clamp(g) + clamp(0.114) * clamp(b);
}

/** Compute the average luma of a flat RGB byte buffer (`Uint8Array` or
 *  number[] of `[r, g, b, r, g, b, ...]`). Returns a value in [0, 1].
 *  Used by the synthetic-frame harness to compare adjacent frames. */
export function averageLumaFromRgb(rgb: ArrayLike<number>): number {
  if (rgb.length === 0) return 0;
  let sum = 0;
  let n = 0;
  for (let i = 0; i + 2 < rgb.length; i += 3) {
    const r = rgb[i] ?? 0;
    const g = rgb[i + 1] ?? 0;
    const b = rgb[i + 2] ?? 0;
    sum += relativeLuma(r / 255, g / 255, b / 255);
    n += 1;
  }
  return n === 0 ? 0 : sum / n;
}

/**
 * Clamp a *target* luma to within `MAX_LUMINANCE_DELTA` of the
 * *previous* luma. The Frenzy renderer calls this every frame when
 * `low_motion === true` so a strobe pulse that would otherwise jump the
 * luma by >10% is softened to the cap. Returns the clamped target.
 *
 * Pure function — easy to unit-test against synthetic frame
 * sequences without a live PixiJS context.
 */
export function clampLuminanceDelta(
  previous: number,
  target: number,
  cap: number = MAX_LUMINANCE_DELTA,
): number {
  const delta = target - previous;
  if (Math.abs(delta) <= cap) return target;
  return delta > 0 ? previous + cap : previous - cap;
}

// ---------------------------------------------------------------------------
// Themed copy
// ---------------------------------------------------------------------------

/** Steampunk-themed hint shown beneath the Reduce-Motion toggle when the
 *  OS already prefers reduced motion. Reads as in-world voice per
 *  `feedback_error_text_immersion`. */
export const REDUCE_MOTION_OS_HINT =
  "The clocktower's gauges already favor a steady hand — your machine asked us to dim the gears.";

/** Steampunk-themed hint shown beneath the Colorblind dropdown,
 *  explaining that shape glyphs ride alongside the palette swap. */
export const COLORBLIND_HINT =
  'Each gear can wear an etched rune so the works read true even when the colors blur.';
